// AdminMedicationsPanel — read-only summary of a child's
// medication consents + recent administration log. Admins can
// withdraw a consent in rare correction cases (the same field a
// parent would have flipped); they cannot edit logs.

import { useEffect, useState } from "react";
import { Pill, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  formatAdministeredAt,
  isAuthorizationActiveToday,
  listAdministrationsForChild,
  listAuthorizationsForChild,
  routeLabel,
  withdrawAuthorization,
} from "@/lib/medications";

export default function AdminMedicationsPanel({ childId, profileId }) {
  const [auths, setAuths] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!childId) return;
    setLoading(true);
    try {
      const [a, l] = await Promise.all([
        listAuthorizationsForChild(childId, { activeOnly: false }),
        listAdministrationsForChild(childId, { limit: 5 }),
      ]);
      setAuths(a);
      setLog(l);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const onWithdraw = async (row) => {
    if (
      !window.confirm(
        `Admin override: withdraw consent for ${row.medication_name}?`,
      )
    )
      return;
    try {
      await withdrawAuthorization({ id: row.id, profileId });
      toast.success("Consent withdrawn");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not withdraw");
    }
  };

  if (loading) return null;
  const active = auths.filter(isAuthorizationActiveToday);

  return (
    <section
      data-testid="admin-medications-panel"
      className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-3"
    >
      <header className="mb-2 flex items-center gap-2">
        <Pill size={14} weight="duotone" className="text-primary-hover" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">
          Medications · {active.length} active
        </p>
      </header>

      {active.length === 0 ? (
        <p
          data-testid="admin-medications-empty"
          className="text-[12px] text-neutral-500"
        >
          No active medication consents.
        </p>
      ) : (
        <ul className="space-y-2">
          {active.map((a) => (
            <li
              key={a.id}
              data-testid={`admin-medications-row-${a.id}`}
              className="rounded-xl border border-neutral-200 bg-white p-2"
            >
              <p className="flex flex-wrap items-baseline gap-1 text-sm font-semibold text-neutral-900">
                {a.medication_name}
                <span className="text-[11px] font-normal text-neutral-500">
                  · {a.dosage} · {routeLabel(a.route)}
                </span>
              </p>
              {a.schedule && (
                <p className="text-[11px] text-neutral-700">{a.schedule}</p>
              )}
              {a.warnings && (
                <p className="mt-1 inline-flex items-start gap-1 rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-900">
                  <Warning size={10} weight="duotone" className="mt-0.5" aria-hidden />
                  {a.warnings}
                </p>
              )}
              <div className="mt-1 flex justify-end">
                <button
                  type="button"
                  data-testid={`admin-medications-withdraw-${a.id}`}
                  onClick={() => onWithdraw(a)}
                  className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
                >
                  Withdraw
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {log.length > 0 && (
        <section className="mt-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
            Recent doses
          </p>
          <ul className="mt-1 space-y-1 text-[11px] text-neutral-700">
            {log.map((r) => (
              <li
                key={r.id}
                data-testid={`admin-medications-log-${r.id}`}
                className="rounded-md border border-neutral-200 bg-white px-1.5 py-0.5"
              >
                <strong>{r.medication_name}</strong> · {r.dose_given} ·{" "}
                {formatAdministeredAt(r.administered_at)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}
